import { useEffect, useState } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import Sidebar from './Sidebar';

const drawerWidthExpanded = 280;
const drawerWidthCollapsed = 80;

export default function MainLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(!isMobile);
  const { isAuthenticated, loading,can,user } = useAuth();
  const {pathname}=useLocation();
  useEffect(() => { setSidebarOpen(!isMobile); }, [isMobile]);
  const required=pathname==="/comercial"?"commercial.manage":pathname==="/faturamento"?"financial.manage":pathname==="/infraestrutura"?"infra.manage":pathname==="/management-users"?"users.manage":undefined;

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const currentDrawerWidth = isMobile
    ? drawerWidthExpanded
    : sidebarOpen
    ? drawerWidthExpanded
    : drawerWidthCollapsed;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: isMobile ? '100%' : `calc(100% - ${currentDrawerWidth}px)`,
          ml: isMobile ? 0 : `${currentDrawerWidth}px`,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          zIndex: isMobile ? theme.zIndex.appBar : theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ gap: 1, minWidth: 0 }}>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleSidebarToggle}
              aria-label="Abrir menu"
              sx={{ mr: 0 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" noWrap component="div" sx={{ minWidth: 0, flex: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            ☀️ Solar Management
          </Typography>
          <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 90, sm: '40%' }, flexShrink: 0 }} title={user?.name}>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{user?.name} · </Box>
            {user?.role==="super_admin"?"SADM":user?.role==="admin"?"ADM":"Operador"}
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Sidebar
        open={sidebarOpen}
        onToggle={handleSidebarToggle}
        onClose={handleSidebarClose}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          p: { xs: 2, sm: 3 },
          mt: { xs: '56px', sm: '64px' },
          width: isMobile ? '100%' : `calc(100% - ${currentDrawerWidth}px)`,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {required&&!can(required)?<Alert severity="warning">Seu perfil não permite acessar esta página.</Alert>:<Outlet />}
      </Box>
    </Box>
  );
}
